'use client';

import { useState } from 'react';
import { Link2 } from 'lucide-react';
import { FacebookIcon, LinkedInIcon, TwitterIcon } from '@/components/ui/icons';
import { cn } from '@/components/ui/utils';

interface BlogShareLinksProps {
  url: string;
  title: string;
  className?: string;
}

const shareLinks = (url: string, title: string) => {
  const whatsAppText = `${title} ${url}`;
  return [
    {
      label: 'Share on X',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      icon: TwitterIcon,
    },
    {
      label: 'Share on LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      icon: LinkedInIcon,
    },
    {
      label: 'Share on Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      icon: FacebookIcon,
    },
    {
      label: 'Share on WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(whatsAppText)}`,
      icon: WhatsAppIcon,
    },
  ];
};

function WhatsAppIcon({ className = '', size = 18 }: { className?: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function BlogShareLinks({ url, title, className }: BlogShareLinksProps) {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={cn('mt-6 border-t border-black/15 pt-5', className)}>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-black/70">Share</h3>
      <div className="mt-3 flex flex-wrap items-center gap-2.5">
        {shareLinks(url, title).map(({ label, href, icon: Icon }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-black/15 bg-white text-black/70 transition hover:border-[#ed1c22] hover:text-[#ed1c22]"
          >
            <Icon size={16} />
          </a>
        ))}
        <button
          type="button"
          onClick={copyUrl}
          aria-label={copied ? 'Link copied' : 'Copy link'}
          className="flex h-9 items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 text-xs font-medium text-black/70 transition hover:border-[#ed1c22] hover:text-[#ed1c22]"
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden />
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}
